import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/auth.css';
import toast from 'react-hot-toast';

const AccountUpgrade = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [upgradeMethod, setUpgradeMethod] = useState('email'); // 'email' or 'google'
  
  const navigate = useNavigate();
  const { 
    linkAnonymousWithEmail, 
    linkAnonymousWithGoogle, 
    isAuthenticated,
    isAnonymous,
    user,
    loading: authLoading,
    emailVerificationSent 
  } = useAuth();

  // Redirect if not anonymous user
  useEffect(() => {
    if (!authLoading && (!isAuthenticated() || !isAnonymous())) {
      navigate('/dashboard');
    }
  }, [authLoading, isAuthenticated, isAnonymous, navigate]);

  const handleEmailUpgrade = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    const result = await linkAnonymousWithEmail(email, password, '');
    setLoading(false);

    if (result.success) {
      // Stay on page to show email verification message
      setEmail('');
      setPassword('');
    }
  };

  const handleGoogleUpgrade = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await linkAnonymousWithGoogle();
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <section className='w-full relative tracking-wide min-h-[80vh] flex justify-center items-center flex-col'>
      {/* Current Status */}
      <div className="account-upgrade-banner" style={{ marginBottom: '20px' }}>
        <div className="flex items-center justify-center mb-3">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontWeight: '600', marginBottom: '4px' }}>You're currently a guest user</p>
            <p style={{ fontSize: '14px' }}>Your data will be lost when you sign out unless you upgrade</p>
          </div>
        </div>
      </div>

      {/* Email verification message */}
      {emailVerificationSent && (
        <div className="email-verification-banner" style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: '600', marginBottom: '8px' }}>Account Upgraded Successfully!</p>
          <p style={{ fontSize: '14px', marginBottom: '12px' }}>
            We've sent a verification email to your inbox. Please verify your email to complete the upgrade.
          </p>
          <button 
            onClick={() => navigate('/dashboard')}
            style={{ 
              backgroundColor: '#f59e0b', 
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Continue to Dashboard
          </button>
        </div>
      )}

      <form className="authorization-form">
        {/* Upgrade method selection */}
        <div style={{ display: 'flex', marginBottom: '20px', backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '4px' }}>
          <button
            type="button"
            onClick={() => setUpgradeMethod('email')}
            style={{
              flex: 1,
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: upgradeMethod === 'email' ? 'white' : 'transparent',
              color: upgradeMethod === 'email' ? '#1f2937' : '#6b7280',
              boxShadow: upgradeMethod === 'email' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
            }}
          >
            Email & Password
          </button>
          <button
            type="button"
            onClick={() => setUpgradeMethod('google')}
            style={{
              flex: 1,
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: upgradeMethod === 'google' ? 'white' : 'transparent',
              color: upgradeMethod === 'google' ? '#1f2937' : '#6b7280',
              boxShadow: upgradeMethod === 'google' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
            }}
          >
            Google Account
          </button>
        </div>

        {upgradeMethod === 'email' ? (
          <>
            <div className="authorization-flex-column">
              <label>Email </label>
            </div>
            <div className="authorization-inputForm">
              <svg height="20" viewBox="0 0 32 32" width="20" xmlns="http://www.w3.org/2000/svg">
                <g id="Layer_3" data-name="Layer 3">
                  <path d="m30.853 13.87a15 15 0 0 0 -29.729 4.082 15.1 15.1 0 0 0 12.876 12.918 15.6 15.6 0 0 0 2.016.13 14.85 14.85 0 0 0 7.715-2.145 1 1 0 1 0 -1.031-1.711 13.007 13.007 0 1 1 5.458-6.529 2.149 2.149 0 0 1 -4.158-.759v-10.856a1 1 0 0 0 -2 0v1.726a8 8 0 1 0 .2 10.325 4.135 4.135 0 0 0 7.83.274 15.2 15.2 0 0 0 .823-7.455zm-14.853 8.13a6 6 0 1 1 6-6 6.006 6.006 0 0 1 -6 6z"></path>
                </g>
              </svg>
              <input
                type="email"
                id="email"
                name="email"
                className="authorization-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={loading}
              />
            </div>

            <div className="authorization-flex-column">
              <label>Password </label>
            </div>
            <div className="authorization-inputForm">
              <svg height="20" viewBox="-64 0 512 512" width="20" xmlns="http://www.w3.org/2000/svg">
                <path d="m336 512h-288c-26.453125 0-48-21.523438-48-48v-224c0-26.476562 21.546875-48 48-48h288c26.453125 0 48 21.523438 48 48v224c0 26.476562-21.546875 48-48 48zm-288-288c-8.8125 0-16 7.167969-16 16v224c0 8.832031 7.1875 16 16 16h288c8.8125 0 16-7.167969 16-16v-224c0-8.832031-7.1875-16-16-16zm0 0"></path>
                <path d="m304 224c-8.832031 0-16-7.167969-16-16v-80c0-52.929688-43.070312-96-96-96s-96 43.070312-96 96v80c0 8.832031-7.167969 16-16 16s-16-7.167969-16-16v-80c0-70.59375 57.40625-128 128-128s128 57.40625 128 128v80c0 8.832031-7.167969 16-16 16zm0 0"></path>
              </svg>
              <input
                type="password"
                id="password"
                name="password"
                className="authorization-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
            </div>

            <button
              type="button"
              className="authorization-button-submit transition-all duration-200"
              onClick={handleEmailUpgrade}
              disabled={loading}
            >
              {loading ? 'Upgrading Account...' : 'Upgrade with Email'}
            </button>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
              Link your Google account to make your guest account permanent
            </p>
            <button
              type="button"
              className="authorization-btn google"
              onClick={handleGoogleUpgrade}
              disabled={loading}
            >
              <svg version="1.1" width="20" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 512 512" xmlSpace="preserve">
                <path style={{ fill: '#FBBB00' }} d="M113.47,309.408L95.648,375.94l-65.139,1.378C11.042,341.211,0,299.9,0,256c0-42.451,10.324-82.483,28.624-117.732h0.014l57.992,10.632l25.404,57.644c-5.317,15.501-8.215,32.141-8.215,49.456C103.821,274.792,107.225,292.797,113.47,309.408z"></path>
                <path style={{ fill: '#518EF8' }} d="M507.527,208.176C510.467,223.662,512,239.655,512,256c0,18.328-1.927,36.206-5.598,53.451c-12.462,58.683-45.025,109.925-90.134,146.187l-0.014-0.014l-73.044-3.727l-10.338-64.535c29.932-17.554,53.324-45.025,65.646-77.911h-136.89V208.176h138.887L507.527,208.176L507.527,208.176z"></path>
                <path style={{ fill: '#28B446' }} d="M416.253,455.624l0.014,0.014C372.396,490.901,316.666,512,256,512c-97.491,0-182.252-54.491-225.491-134.681l82.961-67.91c21.619,57.698,77.278,98.771,142.53,98.771c28.047,0,54.323-7.582,76.87-20.818L416.253,455.624z"></path>
                <path style={{ fill: '#F14336' }} d="M419.404,58.936l-82.933,67.896c-23.335-14.586-50.919-23.012-80.471-23.012c-66.729,0-123.429,42.957-143.965,102.724l-83.397-68.276h-0.014C71.23,56.123,157.06,0,256,0C318.115,0,375.068,22.126,419.404,58.936z"></path>
              </svg>
              {loading ? 'Upgrading Account...' : 'Upgrade with Google'}
            </button>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={{
              fontSize: '14px',
              color: '#6b7280',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Continue as guest for now
          </button>
        </div>
      </form>
    </section>
  );
};

export default AccountUpgrade;